import React, { useState, useCallback } from 'react';
import { JSONTree } from 'react-json-tree';
import { Button, message, Tooltip } from 'antd';
import { useCopyToClipboard } from '../../logic/Utility/useCopyToClipboard';

interface ICopyButtonState
{
	[key: string]: boolean;
}

interface IJsonTreeWithCopyProps
{
	data: any;
	theme?: any;
	invertTheme?: boolean;
	shouldExpandNodeInitially?: (keyPath: readonly any[], data: any, level: number) => boolean;
	[key: string]: any;
}

/**
 * Enhanced JSONTree with copy functionality for all values
 */
export const JSONTreeWithCopy = (props: IJsonTreeWithCopyProps) =>
{
	const { copyToClipboard } = useCopyToClipboard();
	const [copiedStates, setCopiedStates] = useState<ICopyButtonState>({});

	const formatValue = (value: any): string =>
	{
		if (value === null || value === undefined)
		{
			return String(value);
		}
		if (typeof value === 'string')
		{
			return value;
		}
		return JSON.stringify(value, null, 2);
	};

	const handleCopy = useCallback(async (value: any, uniqueKey: string) =>
	{
		const textToCopy = formatValue(value);
		const result = await copyToClipboard(textToCopy);
		
		if (result.success)
		{
			setCopiedStates(prev => ({ ...prev, [uniqueKey]: true }));
			// Reset the copied state after 2 seconds
			setTimeout(() =>
			{
				setCopiedStates(prev => ({ ...prev, [uniqueKey]: false }));
			}, 2000);
		}
		else
		{
			message.error('Failed to copy to clipboard');
		}
	}, [copyToClipboard]);

	// Custom value renderer that adds copy button to leaf values
	const valueRenderer = useCallback((raw: any, value: any, ...keyPath: any[]) =>
	{
		const uniqueKey = [...keyPath].reverse().join('.');
		const isCopied = copiedStates[uniqueKey];
		
		return (
			<span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
				<span>{raw}</span>
				<Tooltip title={isCopied ? 'Copied!' : 'Copy value'}>
					<Button
						type="text"
						size="small"
						style={{
							marginTop: -10,
							padding: '2px',
							height: '16px',
							minWidth: '16px',
							fontSize: '14px',
							opacity: 0.6,
							transition: 'opacity 0.2s',
							color: isCopied ? '#52c41a' : 'white'
						}}
						onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
						onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
						onClick={(e) =>
						{
							e.stopPropagation();
							handleCopy(value, uniqueKey);
						}}
						aria-label={`Copy ${typeof value} value`}
					>
						{isCopied ? '✓' : '⎘'}
					</Button>
				</Tooltip>
			</span>
		);
	}, [handleCopy, copiedStates]);

	// Custom getItemString that adds copy button for objects and arrays
	const getItemString = useCallback((type: string, data: any, itemType: any, itemString: string, keyPath: readonly any[]) =>
	{
		const uniqueKey = `item-${[...keyPath].reverse().join('.')}`;
		const isCopied = copiedStates[uniqueKey];
		
		return (
			<span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
				<span>
					{type === 'Object' ? `{${Object.keys(data).length} ${Object.keys(data).length === 1 ? 'key' : 'keys'}}` : 
						type === 'Array' ? `[${data.length} ${data.length === 1 ? 'item' : 'items'}]` : 
							itemString}
				</span>
				<Tooltip title={isCopied ? 'Copied!' : `Copy entire ${type.toLowerCase()}`}>
					<Button
						type="text"
						size="middle"
						style={{
							marginTop: -6,
							padding: '2px',
							height: '16px',
							minWidth: '16px',
							fontSize: '14px',
							opacity: 0.6,
							transition: 'opacity 0.2s',
							color: isCopied ? '#52c41a' : 'white'
						}}
						onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
						onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
						onClick={(e) =>
						{
							e.stopPropagation();
							handleCopy(data, uniqueKey);
						}}
						aria-label={`Copy entire ${type.toLowerCase()}`}
					>
						{isCopied ? '✓' : '⎘'}
					</Button>
				</Tooltip>
			</span>
		);
	}, [handleCopy, copiedStates]);

	return (
		<JSONTree
			{...props}
			valueRenderer={valueRenderer}
			getItemString={getItemString}
		/>
	);
};
